import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { t } from '../../../utils/i18n';
import {
  CHART_AXIS_STROKE,
  CHART_GRID_STROKE,
  CHART_HEIGHT,
  darkChartTooltipProps,
  formatChartDate,
} from '../../../utils/gaDashboard';
import DashboardPanel from '../components/DashboardPanel';
import ChartSkeleton from '../components/ChartSkeleton';

function PercentTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-md border border-dark-border px-3 py-2 text-sm text-dark-text shadow-lg"
      style={{ backgroundColor: 'rgb(var(--admin-panel))' }}
    >
      <p className="mb-1 text-dark-text-secondary">{formatChartDate(label)}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {entry.value != null ? `${(entry.value * 100).toFixed(1)}%` : '—'}
        </p>
      ))}
    </div>
  );
}

export default function GaMetricLineChart({
  title,
  data,
  lines,
  isLoading,
  yAxisPercent = false,
  heightClass = CHART_HEIGHT.panel,
  className = '',
  showPoints = false,
}) {
  return (
    <DashboardPanel title={title} className={className}>
      {isLoading ? (
        <ChartSkeleton heightClass={heightClass} />
      ) : !data?.length ? (
        <p className={`${heightClass} flex items-center justify-center text-dark-text-secondary`}>
          {t('dashboard.ga.noData')}
        </p>
      ) : (
        <div className={`${heightClass} animate-fade-in`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
              <XAxis
                dataKey="date"
                stroke={CHART_AXIS_STROKE}
                fontSize={11}
                tickFormatter={formatChartDate}
              />
              <YAxis
                stroke={CHART_AXIS_STROKE}
                fontSize={11}
                width={32}
                tickFormatter={yAxisPercent ? (v) => `${(v * 100).toFixed(0)}%` : undefined}
              />
              <Tooltip
                {...(yAxisPercent ? { content: <PercentTooltip /> } : darkChartTooltipProps)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {lines.map((line) => (
                <Line
                  key={line.dataKey}
                  type="monotone"
                  dataKey={line.dataKey}
                  name={line.name}
                  stroke={line.color}
                  strokeWidth={1.75}
                  dot={showPoints ? { r: 2.5, strokeWidth: 0 } : false}
                  isAnimationActive={showPoints ? false : undefined}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </DashboardPanel>
  );
}
